// Shared behaviour helpers for the Fleet Finance pages (QA fixes, Oct 2026).
// Presentational helpers (money, badges, PageHead) stay in ffUi.js.
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import ApiConfig from "../../api/ApiConfig";
import { getWithAuthCall } from "../../api/ApiServices";
import { money } from "./ffUi";

// ---- Dates (FF-06) --------------------------------------------------------
// The API returns business dates as UTC timestamps (e.g. 2026-10-12T18:30:00Z
// for 13-10-2026 in UTC+5:30). Slicing the ISO string loses a day, so convert
// to the local calendar date instead. Plain YYYY-MM-DD strings pass through.
export const toDateInput = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const todayInput = () => toDateInput(new Date());

// dd-mm-yyyy for display without a time-zone shift
export const toDateDisplay = (value) => {
  const ymd = toDateInput(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || "-";
  const [y, m, d] = ymd.split("-");
  return `${d}-${m}-${y}`;
};

// ---- Vehicle list (U-01, performance) ------------------------------------
// One fetch per session, sorted, without blank/undefined names, with the plate
// shown beside the name so duplicates can be told apart.
let vehicleCache = null;
let vehiclePromise = null;

const clean = (s) =>
  s === null || s === undefined || String(s).trim() === "" || String(s) === "undefined" || String(s) === "null"
    ? ""
    : String(s).trim();

export const vehicleLabel = (v) => {
  if (!v) return "";
  const name = clean(v.vehicle_name);
  const plate = clean(v.vehicle_number);
  if (name && plate && name !== plate) return `${name} (${plate})`;
  return name || plate || "";
};

const normaliseVehicles = (rows) => {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : [])
    .filter((v) => v && v.vehicle_id !== undefined && v.vehicle_id !== null)
    .map((v) => ({ ...v, label: vehicleLabel(v) }))
    .filter((v) => v.label)
    .filter((v) => {
      const key = String(v.vehicle_id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
};

export const fetchVehicles = (force = false) => {
  if (vehicleCache && !force) return Promise.resolve(vehicleCache);
  if (vehiclePromise && !force) return vehiclePromise;
  vehiclePromise = getWithAuthCall(ApiConfig.VEHICEL_AVALIVALITY_DROPDAWAN)
    .then((data) => {
      vehicleCache = normaliseVehicles(data?.data);
      return vehicleCache;
    })
    .catch((err) => {
      console.log("vehicle dropdown err", err);
      vehiclePromise = null;
      return vehicleCache || [];
    });
  return vehiclePromise;
};

export const useVehicles = () => {
  const [vehicles, setVehicles] = useState(vehicleCache || []);
  useEffect(() => {
    let alive = true;
    fetchVehicles().then((list) => alive && setVehicles(list));
    return () => {
      alive = false;
    };
  }, []);
  return vehicles;
};

// Name/plate for a vehicle id (U-02). Falls back to the id itself.
export const vehicleNameById = (vehicles, id) => {
  if (id === null || id === undefined || id === "") return "-";
  const n = Number(id);
  const key = Number.isNaN(n) ? String(id) : String(Math.round(n));
  const v = (vehicles || []).find((x) => String(x.vehicle_id) === key);
  return v ? v.label || vehicleLabel(v) : key;
};

// Searchable vehicle picker. Emits a synthetic { target: { name, value } }
// event so it can replace a <Form.Select name=...> without changing handlers.
export const VehicleSelect = ({
  vehicles,
  value,
  onChange,
  name = "vehicle_id",
  placeholder = "Search vehicle by name or plate",
  disabled = false,
  isClearable = true,
  allOption = null, // e.g. "All vehicles" adds an empty first option
}) => {
  const list = vehicles || [];
  const options = [
    ...(allOption ? [{ value: "", label: allOption }] : []),
    ...list.map((v) => ({ value: String(v.vehicle_id), label: v.label || vehicleLabel(v) })),
  ];
  let current = null;
  if (value === null || value === undefined || value === "") {
    current = allOption ? options[0] : null;
  } else {
    current =
      options.find((o) => o.value === String(value)) || {
        value: String(value),
        label: String(value),
      };
  }
  return (
    <Select
      classNamePrefix="ff-select"
      className="ff-select-container"
      options={options}
      value={current}
      isDisabled={disabled}
      isClearable={isClearable && !allOption}
      isSearchable
      placeholder={placeholder}
      noOptionsMessage={() => "No vehicle matches"}
      onChange={(opt) => onChange({ target: { name, value: opt ? opt.value : "" } })}
      // render the menu on <body> so modals / table wrappers don't clip it
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
        menu: (base) => ({ ...base, zIndex: 20 }),
        control: (base) => ({ ...base, minHeight: 38 }),
      }}
    />
  );
};

// ---- Confirmation dialog (FF-10, FF-15, U-08, U-09) ----------------------
export const ConfirmDialog = ({
  show,
  title,
  body,
  confirmText = "Yes",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  busy = false,
}) => (
  <Modal show={show} onHide={onCancel} centered className="common-model">
    <Modal.Header closeButton>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>{body}</Modal.Body>
    <Modal.Footer className="pop-up-modal-footer">
      <div className="btn-wrapper">
        <button type="button" className="cx-btn-1" onClick={onCancel} disabled={busy}>
          {cancelText}
        </button>
        <button type="button" className="cx-btn-2" onClick={onConfirm} disabled={busy}>
          {confirmText}
        </button>
      </div>
    </Modal.Footer>
  </Modal>
);

// ---- Pagination (FF-17, QA #2 / #7) ---------------------------------------
// One bar: "Showing a - b of n" + Previous / Page x of y / Next. Uses its own
// classes, not the global .reg-color (absolutely positioned, covers controls).
export const FfPagination = ({ t, page, totalPages, rangeStart, rangeEnd, total, onPage }) => (
  <div className="ff-pagination">
    <p className="ff-page-info">
      {t("Showing")} {rangeStart} - {rangeEnd} {t("of")} {total}
    </p>
    <div className="ff-page-controls">
      <button
        type="button"
        className="ff-page-btn"
        disabled={page <= 1}
        onClick={() => onPage(Math.max(1, page - 1))}
      >
        &#8249; {t("Previous")}
      </button>
      <span className="ff-page-current">
        {t("Page")} {page} {t("of")} {totalPages}
      </span>
      <button
        type="button"
        className="ff-page-btn"
        disabled={page >= totalPages}
        onClick={() => onPage(Math.min(totalPages, page + 1))}
      >
        {t("Next")} &#8250;
      </button>
    </div>
  </div>
);

// ---- Readable labels (U-03, U-04) ----------------------------------------
// Values the API actually stores (FF-03): account_type uses "revenue",
// statement uses "pnl" / "balance_sheet".
export const ACCOUNT_TYPES = ["asset", "liability", "equity", "revenue", "expense"];
export const STATEMENTS = ["pnl", "balance_sheet"];

export const ACCOUNT_TYPE_LABELS = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  revenue: "Revenue",
  income: "Revenue",
  expense: "Expense",
};
export const STATEMENT_LABELS = {
  pnl: "Profit & Loss",
  balance_sheet: "Balance Sheet",
  bs: "Balance Sheet",
};
export const TCO_BUCKET_LABELS = {
  operating: "Operating",
  ownership: "Ownership",
  acquisition: "Acquisition",
  depreciation: "Depreciation",
  residual: "Residual",
  none: "None",
};

export const FIELD_LABELS = {
  tco: "Total Cost of Ownership",
  tco_lifetime: "TCO (lifetime)",
  tco_to_date: "TCO (to date)",
  tco_basis: "How TCO is calculated",
  depreciation_to_date: "Depreciation to date",
  depreciation_cost: "Depreciation",
  operating_cost_total: "Operating Cost",
  cpk_lifetime: "CPK (lifetime)",
  cpk_to_date: "CPK (to date)",
  km_total: "Total Km",
  period_from: "Period From",
  period_to: "Period To",
  cpk: "Cost per km",
  cpk_variance_pct: "CPK Variance %",
  planned_cpk: "Planned CPK",
  replace_flag: "Replace",
  maintenance_ratio_pct: "Maintenance / Depreciation %",
  maintenance_12m: "Maintenance (12 months)",
  annual_depreciation: "Annual Depreciation",
  register_gross_cost: "Register Gross Cost",
  rou_assets_net: "ROU Assets (Net)",
  retained_earnings_fleet: "Retained Earnings (Fleet)",
  deferred_tax_liability: "Deferred Tax Liability",
  deferred_tax_liability_ledger: "Deferred Tax Liability (Ledger)",
  deferred_tax_liability_basis: "Deferred Tax Liability Basis",
  total_revenue: "Total Revenue",
  total_expenses: "Total Expenses",
  net_income: "Net Income",
  rou_asset_initial: "ROU Asset (initial)",
  lease_liability_initial: "Lease Liability (initial)",
  dtl_amount: "Deferred Tax Liability",
  total_dtl: "Total Deferred Tax Liability",
  nbv: "Net Book Value",
  book_nbv: "Net Book Value",
  net_book_value: "Net Book Value",
  fmv_value: "Fair Market Value",
  olv_value: "Orderly Liquidation Value",
  flv_value: "Forced Liquidation Value",
  acquisition_cost: "Acquisition Cost",
  operating_cost: "Operating Cost",
  ownership_cost: "Ownership Cost",
  residual_value: "Residual Value",
  km_driven: "Km Driven",
  utilization_pct: "Utilization %",
  underutilized: "Under-utilised",
  vehicle_id: "Vehicle",
  vehicle_name: "Vehicle",
  as_of: "As Of",
  impairment_flag: "Impaired",
  impairment_amount: "Impairment Amount",
  timing_difference: "Timing Difference",
  odometer_start: "Odometer Start",
  odometer_end: "Odometer End",
  expected_km: "Expected Km",
  useful_life_months: "Useful Life (months)",
  in_service_date: "In-service Date",
};

export const labelFor = (key) => {
  if (!key) return "";
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return String(key)
    .replace(/_/g, " ")
    .replace(/\bpct\b/gi, "%")
    .replace(/\bcpk\b/gi, "CPK")
    .replace(/\btco\b/gi, "TCO")
    .replace(/\bdtl\b/gi, "DTL")
    .replace(/\brou\b/gi, "ROU")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Render any API value for a read-only cell: booleans -> Yes/No, numbers -> money,
// dates -> dd-mm-yyyy, *_pct -> percentage. Pass `vehicles` to resolve vehicle_id.
export const formatValue = (value, key, vehicles) => {
  if (value === null || value === undefined || value === "") return "-";
  if (key === "vehicle_id" && vehicles) return vehicleNameById(vehicles, value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  const s = String(value);
  if (/^(true|false)$/i.test(s)) return /^true$/i.test(s) ? "Yes" : "No";
  if (key && /date|as_of|period_end|period_start/i.test(key) && /^\d{4}-\d{2}-\d{2}/.test(s))
    return toDateDisplay(s);
  if (/_id$/.test(key || "")) return s;
  if (key && /pct|percent|ratio/i.test(key) && !Number.isNaN(Number(s))) return `${Number(s).toFixed(2)}%`;
  if (s.trim() !== "" && !Number.isNaN(Number(s))) return money(s);
  return s;
};

// ---- Validation (FF-11) ---------------------------------------------------
export const isValidUrl = (s) => {
  try {
    const u = new URL(String(s).trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch (e) {
    return false;
  }
};
export const isAccountCode = (s) => /^[A-Za-z0-9][A-Za-z0-9.-]{0,19}$/.test(String(s || "").trim());
export const isFutureDate = (ymd) => !!ymd && ymd > todayInput();
export const inRange = (n, min, max) => {
  const x = Number(n);
  return !Number.isNaN(x) && x >= min && x <= max;
};
export const isPositiveNumber = (n) => !Number.isNaN(Number(n)) && Number(n) > 0;
export const isNonNegativeNumber = (n) => !Number.isNaN(Number(n)) && Number(n) >= 0;
